// Encode decoded PCM (Float32 channels + sample rate) into a file format that
// plays anywhere without a special codec — the opposite job of wavDecoder.js.
//
// WAV needs no dependency at all: it's a ~44-byte header plus the raw samples.
// MP3 needs a real encoder (lamejs, loaded lazily so it never touches the main
// bundle) and, being CPU-bound pure JS, runs inside audioConvertWorker.js so a
// long file doesn't freeze the tab.
//
// DELIBERATELY DOM-FREE: everything in this file must also work inside a Web
// Worker. Resampling needs OfflineAudioContext, which workers don't have, so
// that step lives in resampleChannels.js and happens on the main thread
// *before* audio reaches encodeMp3 — this module only ever sees a sample rate
// it can already encode.

const clamp16 = v => Math.max(-32768, Math.min(32767, Math.round(v * 32768)));

/** Float32 in [-1, 1] → Int16, one array per channel. */
export function floatTo16BitPCM(channels) {
  return channels.map((data) => {
    const out = new Int16Array(data.length);
    for (let i = 0; i < data.length; i++) out[i] = clamp16(data[i]);
    return out;
  });
}

function writeAscii(view, offset, str) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}

/**
 * Build a standard 16-bit PCM WAV file — the format every media player,
 * editor, and court AV system reads without a codec pack.
 *
 * @param {Float32Array[]} channels
 * @param {number} sampleRate
 * @returns {ArrayBuffer}
 */
export function encodeWavPCM16(channels, sampleRate) {
  const int16 = floatTo16BitPCM(channels);
  const numChannels = int16.length;
  const numFrames = numChannels ? int16[0].length : 0;
  const blockAlign = numChannels * 2;
  const dataSize = numFrames * blockAlign;

  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeAscii(view, 8, 'WAVE');

  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);               // fmt chunk size
  view.setUint16(20, 1, true);                // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);                // bits per sample

  writeAscii(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let f = 0; f < numFrames; f++) {
    for (let c = 0; c < numChannels; c++) {
      view.setInt16(offset, int16[c][f], true);
      offset += 2;
    }
  }

  return buffer;
}

// LAME only encodes at these rates (one per MPEG version/layer combination).
export const MP3_SAMPLE_RATES = [8000, 11025, 12000, 16000, 22050, 24000, 32000, 44100, 48000];

export function nearestMp3Rate(sampleRate) {
  if (MP3_SAMPLE_RATES.includes(sampleRate)) return sampleRate;
  return MP3_SAMPLE_RATES.reduce((best, rate) => (
    Math.abs(rate - sampleRate) < Math.abs(best - sampleRate) ? rate : best
  ));
}

export const MP3_BITRATES = [
  { kbps: 96, label: '96 kbps — smallest file' },
  { kbps: 128, label: '128 kbps — good default' },
  { kbps: 192, label: '192 kbps — better quality' },
  { kbps: 320, label: '320 kbps — highest quality' },
];

export const DEFAULT_MP3_KBPS = 128;

// LAME frame size; the docs recommend feeding it in multiples of this.
const MP3_BLOCK_SIZE = 1152;

/** LAME only encodes mono or stereo — anything wider takes just the first two. */
export function toStereo(channels) {
  return channels.length >= 2 ? [channels[0], channels[1]] : [channels[0]];
}

/**
 * Encode PCM to MP3. `sampleRate` must already be one of MP3_SAMPLE_RATES and
 * `channels` must already be mono or stereo — callers resample and downmix
 * (via resampleChannels + toStereo) before calling this, since that step needs
 * DOM APIs this function deliberately avoids.
 *
 * @param {Float32Array[]} channels  mono or stereo only
 * @param {number} sampleRate        must be in MP3_SAMPLE_RATES
 * @param {Object} [opts]
 * @param {number} [opts.kbps]
 * @param {(fraction:number)=>void} [opts.onProgress]
 * @returns {Promise<Blob>}
 */
export async function encodeMp3(channels, sampleRate, { kbps = DEFAULT_MP3_KBPS, onProgress } = {}) {
  if (!MP3_SAMPLE_RATES.includes(sampleRate)) {
    throw new Error(
      `encodeMp3 requires one of MP3_SAMPLE_RATES, got ${sampleRate} Hz — resample with ` +
      `nearestMp3Rate()/resampleChannels() first.`
    );
  }
  if (channels.length > 2) {
    throw new Error('encodeMp3 accepts mono or stereo only — downmix with toStereo() first.');
  }

  const { Mp3Encoder } = await import('@breezystack/lamejs');

  const int16 = floatTo16BitPCM(channels);
  const numChannels = int16.length;
  const numFrames = numChannels ? int16[0].length : 0;
  const encoder = new Mp3Encoder(numChannels, sampleRate, kbps);

  const parts = [];
  for (let i = 0; i < numFrames; i += MP3_BLOCK_SIZE) {
    const left = int16[0].subarray(i, i + MP3_BLOCK_SIZE);
    const chunk = numChannels === 2
      ? encoder.encodeBuffer(left, int16[1].subarray(i, i + MP3_BLOCK_SIZE))
      : encoder.encodeBuffer(left);
    if (chunk.length) parts.push(chunk);
    onProgress?.(Math.min(1, (i + MP3_BLOCK_SIZE) / Math.max(1, numFrames)));
  }
  const tail = encoder.flush();
  if (tail.length) parts.push(tail);

  return new Blob(parts, { type: 'audio/mpeg' });
}
